import React from 'react';
import PropTypes from 'prop-types';
import { ENGAGEMENT_ANSWERS, EXTRA_QUESTIONS_ANSWERS } from 'constants/engagement';
import i18n from 'i18next';
import QuestionTitle from './QuestionTitle';
import QuestionOption from './QuestionOption';
import CommentArea from './CommentArea';
import './styles.scss';
import { trackMixpanelEvent } from '../../../../utils';
import { MIX_PANEL_EVENTS } from '../../../../constants/mixpanel';

const { ENGAGEMENT_SURVEY } = MIX_PANEL_EVENTS;

class QuestionContent extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            shouldFocusCommentInput: false
        };
    }

    componentDidMount() {
        this.addKeyPressListener();

        trackMixpanelEvent({
            eventName: ENGAGEMENT_SURVEY.ENGAGEMENT_SURVEY_SHOW_QUESTION,
            properties: {
                currentQuestionIndex: this.props.questionIdx,
                questionId: this.props.question.id,
                scaleId: this.props.question.scaleId,
                questionTitle: this.props.question.title,
                hasCommentsEnabled: this.props.hasCommentsEnabled
            }
        });
    }

    componentWillUnmount() {
        this.removeKeyPressListener();
    }

    render() {
        const isCommentInputFocused = this.props.hasCommentsEnabled && this.state.shouldFocusCommentInput;

        return (
            <div className='question-content-container' data-animate={ this.props.direction }>
                <div className='question-content'>
                    <QuestionTitle question={ this.props.question } questionIdx={ this.props.questionIdx } />
                    <p className='tip-text'>
                        {i18n.t('engagement.click_on_button_below', { x: 1, y: this.props.isExtraQuestions ? 7 : 6 })}
                    </p>
                    <div className='answer-options-container'>
                        {(this.props.isExtraQuestions ? EXTRA_QUESTIONS_ANSWERS : ENGAGEMENT_ANSWERS).map((option, idx) =>
                            (<QuestionOption
                                key={ idx }
                                option={ { title: option, value: idx + 1 } }
                                idx={ idx }
                                isSelected={ this.props.isCurrentQuestion && this.props.selectedOption === idx + 1 }
                                selectAnswer={ (opt, e) => this.handleSelectAnswer(opt, e) }
                                hideIcon={ this.props.isExtraQuestions } />))}
                    </div>

                    {
                        this.props.hasCommentsEnabled && (
                            <CommentArea
                                shouldFocusCommentInput={ isCommentInputFocused }
                                clearComment={ this.props.clearComment }
                                questionType={ this.props.question.type }
                                questionIdx={ this.props.questionIdx }
                                questionComment={ this.props.questionComment }
                                handleCommentInput={ this.props.handleCommentInput }
                                handleBlur={ this.blurCommentArea }
                                handleFocus={ this.focusCommentArea } />
                        )
                    }
                </div>
            </div>
        );
    }

    focusCommentArea = () => {
        if (this.state.shouldFocusCommentInput) return;

        this.setState({
            ...this.state,
            shouldFocusCommentInput: true
        });

        trackMixpanelEvent({
            eventName: ENGAGEMENT_SURVEY.ENGAGEMENT_SURVEY_CLICK_COMMENT_BOX,
            properties: {
                currentQuestionIndex: this.props.questionIdx,
                questionId: this.props.question.id,
                scaleId: this.props.question.scaleId,
                questionTitle: this.props.question.title
            }
        });
    }

    blurCommentArea = () => {
        if (!this.state.shouldFocusCommentInput) return;

        this.setState({
            ...this.state,
            shouldFocusCommentInput: false
        });
    }

    handleSelectAnswer = (option, e) => {
        trackMixpanelEvent({
            eventName: ENGAGEMENT_SURVEY.ENGAGEMENT_SURVEY_CLICK_LIKERT,
            properties: {
                selectedEngagementLevel: option,
                interactionType: e.type,
                currentQuestionIndex: this.props.questionIdx,
                questionId: this.props.question.id,
                scaleId: this.props.question.scaleId,
                questionTitle: this.props.question.title
            }
        });

        this.props.selectAnswer(option);
        this.focusCommentArea();
    }

    addKeyPressListener = () => {
        window.addEventListener('keydown', this.handleKeyPress, true);
    }

    removeKeyPressListener = () => {
        window.removeEventListener('keydown', this.handleKeyPress, true);
    }

    handleKeyPress = (e) => {
        if (!this.props.isCurrentQuestion || this.state.shouldFocusCommentInput) return;

        const answer = {
            '1': 1,
            '2': 2,
            '3': 3,
            '4': 4,
            '5': 5,
            '6': 6,
            '7': this.props.isExtraQuestions
                ? this.props.selectAnswer(7)
                : null
        }[e.key] || null;

        this.props.selectAnswer(answer);

        trackMixpanelEvent({
            eventName: ENGAGEMENT_SURVEY.ENGAGEMENT_SURVEY_CLICK_LIKERT,
            properties: {
                selectedEngagementLevel: answer,
                interactionType: e.type,
                currentQuestionIndex: this.props.questionIdx,
                questionId: this.props.question.id,
                scaleId: this.props.question.scaleId,
                questionTitle: this.props.question.title
            }
        });
    }
}

QuestionContent.propTypes = {
    questionComment: PropTypes.string,
    selectedOption: PropTypes.number,
    selectAnswer: PropTypes.func,
    question: PropTypes.object,
    questionIdx: PropTypes.number,
    isCurrentQuestion: PropTypes.bool,
    handleCommentInput: PropTypes.func,
    clearComment: PropTypes.func,
    hasCommentsEnabled: PropTypes.bool,
    isExtraQuestions: PropTypes.bool,
    direction: PropTypes.string
};

QuestionContent.defaultProps = {
    questionComment: null,
    selectedOption: null,
    selectAnswer: null,
    question: {},
    questionIdx: null,
    isCurrentQuestion: false,
    handleCommentInput: null,
    clearComment: null,
    hasCommentsEnabled: null,
    isExtraQuestions: false,
    direction: 'increment'
};

export default QuestionContent;
